import _ from 'lodash';
import {
  ApiFilterOperation,
  ResultDto,
  TableColumn,
  TableFilterState,
} from '@eas/common-web';
import { Me } from '@models';
import { EssRecordState, EvidenceApiUrl, Role } from '@enums';

export function getFilterStateIndexFromColumnsByKey(
  columns: TableColumn<any>[],
  datakey: string
): number {
  const filterableColumns = columns.filter((col) => col.filterable);

  let index = filterableColumns.findIndex(
    (col) => (col.filterkey || col.datakey) === datakey
  );

  if (index === -1) {
    index = filterableColumns.findIndex((col) => col.datakey === datakey);
  }

  return index;
}

export function createInitialFilterState(columns: TableColumn<any>[]) {
  const filterableColumns = columns.filter((col) => col.filterable);

  const filtersState = filterableColumns.map((column) => ({
    enabled: false,
    value: null,
    operation: column.filterOperation ?? ApiFilterOperation.EQ,
  })) as TableFilterState[];

  return filtersState;
}

export const getCurrentUserJudge = (user?: Me) => {
  // when substitution is active, user roles in context are updated, we dont need to do special check
  const judgeRole = user?.userRoles?.find((r) => r.role === 'JUDGE');
  const jid = _.get(judgeRole, 'judge.id');
  const jname = _.get(judgeRole, 'judge.name');
  return { id: jid, name: jname };
};

export const resolvedEssRecordStates = [
  EssRecordState.RESOLVED,
  EssRecordState.CLOSED,
  EssRecordState.FORWARDED_TO_REGISTRY,
  EssRecordState.FORWARDED_TO_ARCHIVE,
  EssRecordState.SHREDDED,
];

export const unresolvedEssRecordStates = [EssRecordState.UNRESOLVED];

export async function fetchJudgeAutocomplete(judge?: {
  id?: string;
  name?: string;
}) {
  const judgeObject = {
    id: judge?.id,
    label: judge?.name ?? '',
    active: true,
  };

  if (!judge?.id) {
    return judgeObject;
  }

  try {
    const response = await fetch(`${EvidenceApiUrl.JUDGES}/autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filters: [
          {
            field: 'id',
            value: judge.id,
            operation: ApiFilterOperation.EQ,
          },
        ],
      }),
    });
    const result = (await response.json()) as ResultDto<any>;
    return result?.items?.[0] ?? judgeObject;
  } catch {
    return judgeObject;
  }
}

export async function fetchAssistantAutocomplete(assistant?: {
  id?: string;
  name?: string;
}) {
  const assistantObject = {
    id: assistant?.id,
    label: assistant?.name ?? '',
    active: true,
  };

  if (!assistant?.id) {
    return assistantObject;
  }

  try {
    const response = await fetch(`${EvidenceApiUrl.USER_ROLE}/autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filters: [
          { operation: 'EQ', field: 'role.id', value: 'ASSISTANT' },
          {
            field: 'id',
            value: assistant.id,
            operation: ApiFilterOperation.EQ,
          },
        ],
      }),
    });
    const result = (await response.json()) as ResultDto<any>;
    return result?.items?.[0] ?? assistantObject;
  } catch {
    return assistantObject;
  }
}

export async function fetchUserRoleAutocomplete({
  id,
  role,
}: {
  id?: string;
  role: Role;
}) {
  const assistantObject = {
    id,
    label: '?',
    active: true,
  };

  if (!id) {
    return assistantObject;
  }

  try {
    const response = await fetch(`${EvidenceApiUrl.USER_ROLE}/autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filters: [
          { operation: 'EQ', field: 'role.id', value: role },
          {
            field: 'id',
            value: id,
            operation: ApiFilterOperation.EQ,
          },
        ],
      }),
    });
    const result = (await response.json()) as ResultDto<any>;
    return result?.items?.[0] ?? assistantObject;
  } catch {
    return assistantObject;
  }
}
